import { useEffect, useState } from "react";
import {
    fetchSupportBanner,
    adminUpsertSupportBanner,
    adminListCannedResponses,
    adminCreateCannedResponse,
    adminUpdateCannedResponse,
    adminDeleteCannedResponse,
    adminListRefunds,
    adminUpdateRefund,
} from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, FileText, CreditCard, Trash2, Plus, Save } from "lucide-react";

const TONE_OPTIONS = [
    { id: "info", label: "Info (teal)" },
    { id: "success", label: "Success (green)" },
    { id: "warning", label: "Warning (amber)" },
    { id: "critical", label: "Critical (rust)" },
];

function SectionHeader({ icon: Icon, title, subtitle }) {
    return (
        <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-bl-primary" />
            </div>
            <div>
                <h3 className="font-display text-bl text-xl leading-tight">{title}</h3>
                {subtitle && (
                    <p className="mt-0.5 text-xs text-bl-muted leading-relaxed">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );
}

function BannerEditor() {
    const [draft, setDraft] = useState({
        message: "",
        tone: "info",
        active: false,
        dismissible: true,
        link_url: "",
        link_label: "",
    });
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const b = await fetchSupportBanner();
                if (b) {
                    setDraft({
                        message: b.message || "",
                        tone: b.tone || "info",
                        active: !!b.active,
                        dismissible: b.dismissible !== false,
                        link_url: b.link_url || "",
                        link_label: b.link_label || "",
                    });
                }
            } finally {
                setLoaded(true);
            }
        })();
    }, []);

    const save = async () => {
        if (!draft.message.trim()) {
            toast.error("Message can't be empty.");
            return;
        }
        setSaving(true);
        try {
            await adminUpsertSupportBanner({
                ...draft,
                message: draft.message.trim(),
                link_url: draft.link_url || null,
                link_label: draft.link_label || null,
            });
            toast.success("Banner saved.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save banner.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bl-card p-5 md:p-6" data-testid="support-banner-editor">
            <SectionHeader
                icon={Megaphone}
                title="Sitewide support banner"
                subtitle="Shows at the very top of every public page. Use sparingly — it's most effective when calm and specific."
            />
            <div className="space-y-3">
                <textarea
                    rows={2}
                    value={draft.message}
                    onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                    maxLength={400}
                    placeholder="e.g. Investor Plus subscribers — Calibration share-back is now live."
                    className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                    data-testid="support-banner-editor-message"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                        value={draft.tone}
                        onChange={(e) => setDraft({ ...draft, tone: e.target.value })}
                        className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                        data-testid="support-banner-editor-tone"
                    >
                        {TONE_OPTIONS.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                    <div className="flex items-center gap-4 text-sm">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={draft.active}
                                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                                data-testid="support-banner-editor-active"
                            />
                            <span className="text-bl">Active</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={draft.dismissible}
                                onChange={(e) => setDraft({ ...draft, dismissible: e.target.checked })}
                            />
                            <span className="text-bl">Dismissible</span>
                        </label>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                        type="text"
                        placeholder="Link URL (optional)"
                        value={draft.link_url}
                        onChange={(e) => setDraft({ ...draft, link_url: e.target.value })}
                        className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                        maxLength={500}
                    />
                    <input
                        type="text"
                        placeholder="Link label (optional)"
                        value={draft.link_label}
                        onChange={(e) => setDraft({ ...draft, link_label: e.target.value })}
                        className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                        maxLength={80}
                    />
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !loaded}
                    className="bl-btn-primary text-sm"
                    data-testid="support-banner-editor-save"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Saving…" : "Save banner"}
                </button>
            </div>
        </div>
    );
}

function CannedResponseManager() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ name: "", category: "", body: "" });
    const [adding, setAdding] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await adminListCannedResponses();
            setRows(r || []);
        } catch {
            toast.error("Could not load canned responses.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const add = async () => {
        if (!draft.name.trim() || !draft.body.trim()) {
            toast.error("Name and body are required.");
            return;
        }
        setAdding(true);
        try {
            await adminCreateCannedResponse({
                name: draft.name.trim(),
                category: draft.category.trim() || null,
                body: draft.body.trim(),
            });
            setDraft({ name: "", category: "", body: "" });
            refresh();
            toast.success("Saved.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save.");
        } finally {
            setAdding(false);
        }
    };

    const onPatch = async (id, patch) => {
        try {
            await adminUpdateCannedResponse(id, patch);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update.");
        }
    };

    const onDelete = async (id) => {
        if (!confirm("Delete this canned response?")) return;
        try {
            await adminDeleteCannedResponse(id);
            refresh();
        } catch {
            toast.error("Delete failed.");
        }
    };

    return (
        <div className="bl-card p-5 md:p-6" data-testid="canned-response-manager">
            <SectionHeader
                icon={FileText}
                title="Canned responses"
                subtitle="Reusable templates that appear inside the ticket-note dropdown for fast replies."
            />
            <div className="space-y-3">
                <input
                    type="text"
                    placeholder="Name (e.g. Refund acknowledged)"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    maxLength={120}
                    data-testid="canned-new-name"
                />
                <input
                    type="text"
                    placeholder="Category (optional — e.g. refund / report_access)"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    maxLength={60}
                    data-testid="canned-new-category"
                />
                <textarea
                    rows={4}
                    placeholder="Body — paste the reply text here."
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                    maxLength={5000}
                    data-testid="canned-new-body"
                />
                <button
                    type="button"
                    onClick={add}
                    disabled={adding}
                    className="bl-btn-primary text-sm"
                    data-testid="canned-new-save"
                >
                    <Plus className="w-4 h-4" />
                    Save canned response
                </button>
            </div>

            <div className="mt-6">
                {loading ? (
                    <p className="text-xs text-bl-muted">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-bl-muted">
                        No canned responses yet. Add one above.
                    </p>
                ) : (
                    <ul className="space-y-3" data-testid="canned-response-list">
                        {rows.map((c) => (
                            <li
                                key={c.canned_id}
                                className="bl-card p-3"
                                data-testid={`canned-row-${c.canned_id}`}
                            >
                                <div className="flex items-start gap-3 flex-wrap">
                                    <input
                                        type="text"
                                        defaultValue={c.name}
                                        onBlur={(e) => {
                                            if (e.target.value !== c.name) {
                                                onPatch(c.canned_id, { name: e.target.value });
                                            }
                                        }}
                                        className="flex-1 min-w-[160px] px-2 py-1 rounded border border-bl bg-bl-surface text-bl text-sm font-medium"
                                    />
                                    <input
                                        type="text"
                                        defaultValue={c.category || ""}
                                        placeholder="category"
                                        onBlur={(e) => {
                                            if (e.target.value !== (c.category || "")) {
                                                onPatch(c.canned_id, {
                                                    category: e.target.value || null,
                                                });
                                            }
                                        }}
                                        className="w-40 px-2 py-1 rounded border border-bl bg-bl-surface text-bl text-xs"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => onDelete(c.canned_id)}
                                        className="text-bl-muted hover:text-[#C85B3A] p-1"
                                        aria-label="Delete"
                                        data-testid={`canned-delete-${c.canned_id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <textarea
                                    defaultValue={c.body}
                                    rows={3}
                                    onBlur={(e) => {
                                        if (e.target.value !== c.body) {
                                            onPatch(c.canned_id, { body: e.target.value });
                                        }
                                    }}
                                    className="mt-2 w-full px-2 py-1.5 rounded border border-bl bg-bl-surface text-bl text-sm resize-y"
                                    maxLength={5000}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function RefundQueue() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);

    const refresh = async (st = status) => {
        setLoading(true);
        try {
            const r = await adminListRefunds(st ? { status: st } : {});
            setRows(r.refund_requests || []);
            setCounts(r.counts || {});
        } catch {
            toast.error("Could not load refunds.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh(status);
        // eslint-disable-next-line
    }, [status]);

    const onPatch = async (id, patch) => {
        try {
            await adminUpdateRefund(id, patch);
            refresh(status);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed.");
        }
    };

    const STATUSES = ["pending", "approved", "denied", "processed"];

    return (
        <div className="bl-card p-5 md:p-6" data-testid="refund-queue">
            <SectionHeader
                icon={CreditCard}
                title="Refund queue"
                subtitle="Recorded refund workflows. Stripe refunds are processed manually in the Stripe dashboard — mark `processed` here once complete."
            />
            <div className="flex flex-wrap gap-2 mb-3">
                <button
                    type="button"
                    onClick={() => setStatus("")}
                    className={
                        "px-2.5 py-1 rounded-full text-xs " +
                        (status === ""
                            ? "bg-bl-primary text-bl-surface"
                            : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                    }
                    data-testid="refund-filter-all"
                >
                    All ({Object.values(counts).reduce((a, b) => a + b, 0)})
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={
                            "px-2.5 py-1 rounded-full text-xs " +
                            (s === status
                                ? "bg-bl-primary text-bl-surface"
                                : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                        }
                        data-testid={`refund-filter-${s}`}
                    >
                        {s} ({counts[s] || 0})
                    </button>
                ))}
            </div>
            {loading ? (
                <p className="text-xs text-bl-muted">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-bl-muted">No refund requests in this view.</p>
            ) : (
                <ul className="space-y-3" data-testid="refund-list">
                    {rows.map((r) => (
                        <li key={r.refund_id} className="bl-card p-3" data-testid={`refund-row-${r.refund_id}`}>
                            <div className="flex items-start gap-3 flex-wrap">
                                <span className="font-mono-bl text-xs text-bl-muted">
                                    {r.refund_id.slice(0, 16)}
                                </span>
                                {r.ticket_id && (
                                    <span className="text-xs text-bl-muted">
                                        ticket {r.ticket_id.slice(0, 12)}
                                    </span>
                                )}
                                {r.amount_usd != null && (
                                    <span className="text-sm font-medium text-bl">
                                        ${Number(r.amount_usd).toFixed(2)}
                                    </span>
                                )}
                                <span className="ml-auto text-xs text-bl-muted">
                                    {new Date(r.created_at).toLocaleString()}
                                </span>
                            </div>
                            {r.reason && (
                                <p className="mt-2 text-sm text-bl whitespace-pre-wrap">{r.reason}</p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2 items-center">
                                <select
                                    value={r.status}
                                    onChange={(e) => onPatch(r.refund_id, { status: e.target.value })}
                                    className="px-2 py-1 rounded border border-bl bg-bl-surface text-bl text-xs"
                                    data-testid={`refund-status-${r.refund_id}`}
                                >
                                    {STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                {r.report_id && (
                                    <a
                                        href={`/report/${r.report_id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-bl-primary hover:underline"
                                    >
                                        View report ↗
                                    </a>
                                )}
                            </div>
                            <textarea
                                placeholder="Admin notes (internal)"
                                defaultValue={r.admin_notes || ""}
                                rows={2}
                                onBlur={(e) => {
                                    if (e.target.value !== (r.admin_notes || "")) {
                                        onPatch(r.refund_id, { admin_notes: e.target.value });
                                    }
                                }}
                                className="mt-2 w-full px-2 py-1.5 rounded border border-bl bg-bl-surface text-bl text-xs resize-y"
                                maxLength={5000}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function SupportSettings() {
    return (
        <div className="space-y-6" data-testid="support-settings">
            <BannerEditor />
            <CannedResponseManager />
            <RefundQueue />
        </div>
    );
}
